/* SIZESOFTX
=========================================================================================
* Descripción: Contexto de autenticación master del panel de soporte
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 17/09/2026 | Muse Spark                       | Sesión en sessionStorage + expiración por
*              |            |                                  | inactividad y validación de perfil al arrancar.
* ---------------------------------------------------------------------------------------
*/

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiLogin, apiLogout, apiPerfil } from "../settings/supportApi";
import {
  clearSession,
  getLastActivity,
  getLoginAt,
  getSessionToken,
  getSessionUser,
  setSession,
  touchActivity,
} from "../settings/supportSession";

// Seguridad de sesión (ajustable):
const INACTIVIDAD_MINUTOS = 20; // sin actividad -> cierre automático
const SESION_MAX_HORAS = 8; // duración máxima aunque haya actividad

const SupportAuthContext = createContext({
  user: null,
  loading: false,
  authReady: false,
  isAuthenticated: false,
  login: async () => {},
  logout: async () => {},
  refreshPerfil: async () => {},
  hasRole: () => false,
});

export const useSupportAuth = () => useContext(SupportAuthContext);

export const SupportAuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getSessionUser());
  const [loading, setLoading] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const logoutRef = useRef(null);

  const logout = useCallback(async () => {
    try {
      if (getSessionToken()) {
        await apiLogout();
      }
    } catch {
      // ignore network errors al cerrar sesión
    }
    clearSession();
    setUser(null);
  }, []);

  logoutRef.current = logout;

  const login = useCallback(async (usuario, password) => {
    const data = await apiLogin(usuario, password);
    if (data && data.token) {
      // Limpia cualquier resto legacy en localStorage antes de guardar en sesión
      clearSession();
      setSession(data.token, data.usuario || {});
      setUser(data.usuario || {});
    }
    return data;
  }, []);

  const refreshPerfil = useCallback(async () => {
    if (!getSessionToken()) {
      setUser(null);
      return null;
    }
    setLoading(true);
    try {
      const perfil = await apiPerfil();
      if (perfil) {
        const token = getSessionToken();
        setSession(token, perfil);
        setUser(perfil);
      }
      return perfil;
    } catch {
      // token inválido/expirado: el interceptor limpia y redirige a /login
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Al arrancar: si hay token de sesión, validarlo contra el backend.
  // Si no hay token (pestaña nueva tras cerrar), queda sin sesión -> pide login.
  useEffect(() => {
    let activo = true;
    const validar = async () => {
      // Limpieza legacy una sola vez: un token viejo en localStorage NO debe revivir la sesión.
      try {
        window.localStorage.removeItem("support_token");
        window.localStorage.removeItem("support_user");
      } catch {
        // ignorar
      }
      if (!getSessionToken()) {
        if (activo) {
          setUser(null);
          setAuthReady(true);
        }
        return;
      }
      try {
        const perfil = await apiPerfil();
        if (activo) {
          if (perfil) {
            const token = getSessionToken();
            setSession(token, perfil);
            setUser(perfil);
          } else {
            clearSession();
            setUser(null);
          }
        }
      } catch {
        if (activo) {
          clearSession();
          setUser(null);
        }
      } finally {
        if (activo) setAuthReady(true);
      }
    };
    validar();
    return () => {
      activo = false;
    };
  }, []);

  // Expiración: inactividad + duración máxima. Revisa cada 30 segundos.
  useEffect(() => {
    const eventos = ["click", "keydown", "mousemove", "scroll", "touchstart"];
    const onActividad = () => touchActivity();
    eventos.forEach((e) => window.addEventListener(e, onActividad, { passive: true }));
    const timer = window.setInterval(() => {
      const token = getSessionToken();
      if (!token) return;
      const ahora = Date.now();
      const ultima = getLastActivity() || getLoginAt() || ahora;
      const inicio = getLoginAt() || ahora;
      const inactivaMs = ahora - ultima;
      const totalMs = ahora - inicio;
      if (inactivaMs > INACTIVIDAD_MINUTOS * 60 * 1000 || totalMs > SESION_MAX_HORAS * 60 * 60 * 1000) {
        if (logoutRef.current) logoutRef.current();
        window.location.hash = "#/login";
      }
    }, 30000);
    return () => {
      eventos.forEach((e) => window.removeEventListener(e, onActividad));
      window.clearInterval(timer);
    };
  }, []);

  const hasRole = useCallback(
    (rol) => {
      const requerido = String(rol || "").trim().toUpperCase();
      return Array.isArray(user?.roles) && user.roles.some((r) => String(r || "").trim().toUpperCase() === requerido);
    },
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      authReady,
      isAuthenticated: Boolean(getSessionToken() && user),
      login,
      logout,
      refreshPerfil,
      hasRole,
    }),
    [user, loading, authReady, login, logout, refreshPerfil, hasRole]
  );

  return <SupportAuthContext.Provider value={value}>{children}</SupportAuthContext.Provider>;
};
